import React, { useEffect, useState } from 'react';
import { FundedSideCardDTO } from '../../../services/get/fundedChallenges/models';
import { AccountBase } from '../../../services/get/challenges/models';
import { green, yellow } from '../../../../../themes/colors/colors';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import classes from '../card.module.scss';
import UpgradeButton from '../../UpgradeButton/UpgradeButton';
import GetPayoutModal from '../../GetPayoutModal/GetPayoutModal';
import ScaleUpModal from '../../ScaleUpModal/ScaleUpModal';
import CredentialsModal from '../../CredentialsModal/CredentialsModal';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ScienceIcon from '@mui/icons-material/Science';

interface ChallengeCardProps {
  onSelect: (accountLogin: string) => void;
  onResetDate: () => void; // Fonction de reset du selectedDate
  challenges: (AccountBase | FundedSideCardDTO)[];
  isFunded: boolean;
  selectedAccountLogin: string | null;
}

const ChallengeCard: React.FC<ChallengeCardProps> = ({
  onSelect,
  onResetDate,
  challenges,
  isFunded,
  selectedAccountLogin,
}) => {
  const { t } = useTranslation(['area', 'paymentStatusPage']);

  const [openCredentials, setOpenCredentials] = useState<boolean>(false);
  const [openPayoutModal, setOpenPayoutModal] = useState<boolean>(false);
  const [openScaleUpModal, setOpenScaleUpModal] = useState<boolean>(false);
  const [visibleStates, setVisibleStates] = useState<{
    [key: string]: boolean;
  }>({});
  const [currentSelectedLogin, setCurrentSelectedLogin] = useState<
    string | null
  >(null);
  const [showInactive, setShowInactive] = useState<boolean>(false); // État pour afficher/masquer les inactives

  const theme = useTheme();
  const navigate = useNavigate();

  const handleSwitchChange = (login: string) => {
    setVisibleStates((prevStates) => ({
      ...prevStates,
      [login]: !prevStates[login],
    }));
  };

  const handleGoToPaymentStatusPage = (externalPaymentId: string) => {
    navigate(`/buy-challenge/payment-status/${externalPaymentId}`);
  };

  const handleOpenCredentials = (login: string): void => {
    setCurrentSelectedLogin(login);
    setOpenCredentials(true);
  };

  const handleCloseCredentials = (): void => {
    setOpenCredentials(false);
  };

  const handleOpenPayoutModal = (login: string): void => {
    setCurrentSelectedLogin(login);
    setOpenPayoutModal(true);
  };

  const handleClosePayoutModal = (): void => {
    setOpenPayoutModal(false);
  };

  const handleOpenScaleUpModal = (login: string): void => {
    setCurrentSelectedLogin(login);
    setOpenScaleUpModal(true);
  };

  const handleCloseScaleUpModal = (): void => {
    setOpenScaleUpModal(false);
  };

  const isAccountBase = (
    challenge: AccountBase | FundedSideCardDTO,
  ): challenge is AccountBase => {
    return (challenge as AccountBase).phase !== undefined;
  };

  const sortedChallenges = [...challenges].sort((a, b) => {
    const aInactive = isAccountBase(a)
      ? a.phase === 'FAILED'
      : (a as FundedSideCardDTO).status === 'INACTIVE';
    const bInactive = isAccountBase(b)
      ? b.phase === 'FAILED'
      : (b as FundedSideCardDTO).status === 'INACTIVE';

    return aInactive === bInactive ? 0 : aInactive ? 1 : -1;
  });

  const handleCardClick = (login: string) => {
    onSelect(login); // Sélectionner le compte
    onResetDate(); // Réinitialiser la date sélectionnée
  };

  return (
    <>
      {sortedChallenges.map((challenge) => {
        const login = challenge.login;
        const balance = challenge.balance;

        let phase: string | undefined;
        let externalPaymentId: string = '';

        if (isAccountBase(challenge)) {
          phase = challenge.phase;
          externalPaymentId = challenge.challengeUUID;
        }

        const isSelectCommission =
          phase !== undefined && phase === 'SELECT_COMMISSION';

        const isAccountInCreation = phase !== undefined && phase === 'CREATING';
        const isInactive =
          phase === 'FAILED' ||
          (challenge as FundedSideCardDTO).status === 'INACTIVE';

        // Masquer les cartes inactives sauf si `showInactive` est activé
        if (!showInactive && isInactive) {
          return null;
        }

        let label: string;

        if (isFunded) {
          const status = (challenge as FundedSideCardDTO).status;
          switch (status) {
            case 'ACTIVE':
              label = t('CHALLENGE_CARD.CHIP_STATUS.ACTIVE');
              break;
            default:
              label = t('CHALLENGE_CARD.CHIP_STATUS.INACTIVE');
          }
        } else {
          const phase = (challenge as AccountBase).phase;
          switch (phase) {
            case 'PHASE_1':
              label = t('CHALLENGE_CARD.CHIP_STATUS.PHASE_1');
              break;
            case 'FUNDED_REQUESTED':
              label = t('CHALLENGE_CARD.CHIP_STATUS.FUNDED_REQUESTED');
              break;
            case 'AWAITING_CONTRACT':
              label = t('CHALLENGE_CARD.CHIP_STATUS.AWAITING_CONTRACT');
              break;
            case 'FAILED':
              label = t('CHALLENGE_CARD.CHIP_STATUS.INACTIVE');
              break;
            default:
              label = t('CHALLENGE_CARD.CHIP_STATUS.PHASE_2');
          }
        }

        const chipBackgroundColor =
          isFunded && (challenge as FundedSideCardDTO).status === 'ACTIVE'
            ? green[300]
            : (challenge as AccountBase).phase === 'PHASE_1'
            ? yellow[600]
            : (challenge as AccountBase).phase === 'PHASE_2'
            ? yellow[800]
            : (challenge as AccountBase).phase === 'SELECT_COMMISSION'
            ? yellow[50]
            : '#a4a4a4';

        const isSelected = login === selectedAccountLogin;

        return (
          <Stack sx={{ position: 'relative' }} key={login}>
            <Stack
              direction={'row'}
              alignItems={'center'}
              gap={1}
              sx={{
                display: (challenge as AccountBase).freeTrial ? 'flex' : 'none',
                position: 'absolute',
                width: 'fit-content',
                backgroundColor: '#4979D1',
                borderRadius: '2px',
                padding: '0px 8px',
                top: '-10px',
                left: '15px',
              }}
            >
              <ScienceIcon />
              <Typography variant="caption" component={'p'} fontWeight={'bold'}>
                FREE TRIAL |{' '}
                {t('CHALLENGE_CARD.DAYS_REMAINING', {
                  days: (challenge as AccountBase).daysRemaining,
                })}
              </Typography>
            </Stack>
            <Card
              className={classes.card}
              elevation={0}
              sx={{
                filter:
                  isSelectCommission || isAccountInCreation
                    ? 'blur(5px)'
                    : visibleStates[challenge.login]
                    ? 'blur(5px)'
                    : 'none',
                transition: 'filter 0.3s',
                pointerEvents:
                  isSelectCommission || isAccountInCreation
                    ? 'none'
                    : visibleStates[challenge.login]
                    ? 'none'
                    : 'auto',
                border:
                  (challenge as AccountBase).freeTrial && isSelected
                    ? '2px solid #4979D1'
                    : isSelected
                    ? '2px solid #3b89f7'
                    : '2px solid transparent',
                '&:hover': {
                  border: (challenge as AccountBase).freeTrial
                    ? '2px solid #4979D1'
                    : '2px solid #3b89f7',
                },
              }}
              onClick={() => handleCardClick(login)}
            >
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignContent="flex-start"
                >
                  <Typography variant="h5" component="h3">
                    {t('CHALLENGE_CARD.TITLE_ACCOUNT')} {login}
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    {!isFunded && (challenge as AccountBase).canUpgrade && (
                      <UpgradeButton
                        accountUUID={(challenge as AccountBase).challengeUUID}
                        phase={(challenge as AccountBase).phase}
                        isFreeTrial={(challenge as AccountBase).freeTrial}
                      />
                    )}
                    {isFunded && (challenge as FundedSideCardDTO).canPayout && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleOpenPayoutModal(login)}
                      >
                        {t('CHALLENGE_CARD.BUTTONS.GET_PAYOUT')}
                      </Button>
                    )}
                    {isFunded &&
                      (challenge as FundedSideCardDTO).canScaleUp && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleOpenScaleUpModal(login)}
                          sx={{
                            backgroundColor: green[300],
                            '&:hover': { backgroundColor: green[400] },
                          }}
                        >
                          {t('CHALLENGE_CARD.BUTTONS.SCALE_UP')}
                        </Button>
                      )}
                    <Chip
                      label={label}
                      sx={{
                        backgroundColor: chipBackgroundColor,
                        color: 'black',
                      }}
                    />
                  </Stack>
                </Stack>
                <Typography
                  variant="subtitle1"
                  component="h4"
                  color="text.secondary"
                >
                  {t('CHALLENGE_CARD.TITLE_BALANCE')} ${balance}
                </Typography>
              </CardContent>
              <CardActions className={classes.actions}>
                <Button
                  onClick={() => handleOpenCredentials(login)}
                  variant="text"
                  size="large"
                  startIcon={<LayersIcon />}
                >
                  {t('CHALLENGE_CARD.BUTTONS.CREDENTIALS')}
                </Button>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={visibleStates[challenge.login] || false}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => handleSwitchChange(challenge.login)}
                      />
                    }
                    label={t('CHALLENGE_CARD.BUTTONS.CHECKBOX_VISIBLE')}
                  />
                </FormGroup>
              </CardActions>
            </Card>
            {(visibleStates[challenge.login] ||
              isSelectCommission ||
              isAccountInCreation) && (
              <Stack className={classes.hidden}>
                <Button
                  variant="contained"
                  color="secondary"

                  onClick={() => {
                    if (isAccountInCreation) return;
                    isSelectCommission
                      ? handleGoToPaymentStatusPage(externalPaymentId)
                      : handleSwitchChange(challenge.login);
                  }}
                >
                  {isAccountInCreation
                    ? t('paymentStatusPage:CARD.ACCOUNT_CREATION_PENDING')
                    : isSelectCommission
                    ? t('paymentStatusPage:CARD.CHOOSE_ACCOUNT_TYPE_BUTTON')
                    : t('CHALLENGE_CARD.BUTTONS.SHOW_ACCOUNT')}
                </Button>
              </Stack>
            )}
          </Stack>
        );
      })}

      {/* Bouton SHOW/HIDE INACTIVE */}
      <Button
        variant="text"
        color="primary"
        onClick={() => setShowInactive(!showInactive)}
        sx={{ alignSelf: 'center' }}
      >
        {showInactive
          ? t('CHALLENGE_CARD.BUTTONS.HIDE_INACTIVE')
          : t('CHALLENGE_CARD.BUTTONS.SHOW_INACTIVE')}
      </Button>

      {currentSelectedLogin && (
        <CredentialsModal
          open={openCredentials}
          handleClose={handleCloseCredentials}
          selectedAccountLogin={currentSelectedLogin}
          isFunded={isFunded}
        />
      )}

      {isFunded && currentSelectedLogin && (
        <GetPayoutModal
          open={openPayoutModal}
          onClose={handleClosePayoutModal}
          accountUUID={Number(currentSelectedLogin)}
          accessToken="e"
        />
      )}

      {isFunded && currentSelectedLogin && (
        <ScaleUpModal
          open={openScaleUpModal}
          onClose={handleCloseScaleUpModal}
          accountUUID={currentSelectedLogin}
        />
      )}
    </>
  );
};

export default ChallengeCard;
